import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Header,
  Segment,
  Card,
  Icon,
  Button,
  Divider,
  Table,
  Label,
  Input,
  Dropdown,
  Modal,
  Message,
  List,
  Form,
  Checkbox,
  Progress,
  Step,
  TextArea
} from 'semantic-ui-react';
import { AuthStdFooter } from "../../utils/auth-std-footer";
import useRoleNormalization from '../../hooks/useRoleNormalization';
import '../../styles/landing-pages.css';
import '../../styles/article.css';
import '../../styles/delete-forms.css';
// import userPuzzle from '../../assets/userPuzzle.svg';
import warningIcon from '../../assets/info_icon_red.svg';
// import trashIcon from '../../assets/clear_text_fields_x.svg';
// import checkBox from '../../assets/checkListLine.svg';

// Import RealUserService for fetching user data
import realUserService from '../../components/messaging/services/RealUserService';
// Import apiClient for API calls
import apiClient from '../../apiTools/apiClient';

// Friendly labels for the GDPR cascade steps (keys of complianceReport.stepResults).
const STEP_LABELS = {
  messages: 'Messages',
  applications: 'Applications',
  postedOpportunities: 'Posted opportunities',
  voiceActorTree: 'Voice actor tree',
  bookCharacterTrees: 'Book character trees',
  prototypes: 'Prototypes',
  bookInfrastructure: 'Book infrastructure',
  foldersAndSamples: 'Folders & samples',
  s3Cleanup: 'S3 cleanup',
  userAccount: 'User account',
};

const DeleteUser = () => {
  const userProfile = useSelector((state) => state.user.profile);
  const navigate = useNavigate();

  // Initialize role normalization hook
  const {
    normalizeRole,
    getRoleColor,
    getRoleIcon,
    getRoleLabel,
    getRoleFilterOptions
  } = useRoleNormalization();

  // State management
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [validationFilter, setValidationFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deleteResult, setDeleteResult] = useState(null); // real results snapshot for the Complete step
  const [confirmationChecks, setConfirmationChecks] = useState({
    understandConsequences: false,
    backupCreated: false,
    notificationsSent: false,
    finalConfirmation: false
  });
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteProgress, setDeleteProgress] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load users on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  // Filter users when search term, role filter, or validation filter changes
  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter, validationFilter]);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      const currentUser = userProfile?.data ? {
        userId: userProfile.data.userId || userProfile.data.id,
        ...userProfile.data
      } : null;

      // Use admin-specific method to get ALL users including incomplete ones
      const allUsers = await realUserService.getAllUsersForAdmin(currentUser);

      if (allUsers && Array.isArray(allUsers)) {
        setUsers(allUsers);
      } else {
        throw new Error('No user data received');
      }
    } catch (err) {
      console.error('Failed to load users:', err);
      setError(`Failed to load users: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.userId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply role filter using normalized roles
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => {
        const userRole = normalizeRole(user.role || user.primaryRole);
        return userRole === roleFilter;
      });
    }

    // Apply validation filter
    if (validationFilter !== 'all') {
      filtered = filtered.filter(user => {
        switch (validationFilter) {
          case 'complete':
            return user.isProfileComplete === true;
          case 'incomplete':
            return user.isProfileComplete === false;
          case 'issues':
            return user.hasDataIssues === true;
          default:
            return true;
        }
      });
    }

    setFilteredUsers(filtered);
  };

  // Helper function to check if user is the currently logged-in administrator
  const isCurrentUser = (user) => {
    const currentUserId = userProfile?.data?.userId || userProfile?.data?.id;
    return currentUserId && user.userId === currentUserId;
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setDeleteStep(1);
    setConfirmationChecks({
      understandConsequences: false,
      backupCreated: false,
      notificationsSent: false,
      finalConfirmation: false
    });
    setDeleteReason('');
    setDeleteProgress(0);
    setIsDeleting(false);
    setShowDeleteModal(true);
  };

  const handleConfirmationChange = (checkName, checked) => {
    setConfirmationChecks(prev => ({
      ...prev,
      [checkName]: checked
    }));
  };

  const canProceedToNextStep = () => {
    switch (deleteStep) {
      case 1:
        return deleteReason.trim().length > 10;
      case 2:
        return confirmationChecks.understandConsequences &&
          confirmationChecks.backupCreated &&
          confirmationChecks.notificationsSent;
      case 3:
        return confirmationChecks.finalConfirmation;
      default:
        return false;
    }
  };

  const proceedToNextStep = () => {
    if (canProceedToNextStep()) {
      setDeleteStep(prev => prev + 1);
    }
  };

  const executeUserDeletion = async () => {
    setIsDeleting(true);
    setError('');
    setDeleteProgress(15);

    try {
      // The server performs the whole GDPR cascade in one request; show
      // indeterminate progress while it runs rather than a fabricated timeline.
      setDeleteProgress(45);
      // A GDPR delete is the LONGEST cascade in the app — every LitGroup the user owns, every book
      // in each, and both S3 roots per book. The default 30s ceiling throws long before that
      // finishes, and on throw the success path never runs: the operator is shown a failure for a
      // delete that succeeded, on the least reversible action there is. Bounded at 15 minutes, the
      // same treatment the LitGroup delete got. NOTE apiClient.delete is (url, params, config).
      const response = await apiClient.delete(`/delete/user/${selectedUser.userId}`, {}, { timeout: 900000 });
      setDeleteProgress(90);

      const data = response.data || {};
      const report = data.complianceReport || {};
      // Distinguish outcomes from the REAL top-level status ('success' |
      // 'partial_success' | 'failure'). A 207 (partial) now resolves instead
      // of throwing, so partial deletions land here, not in the catch.
      const status = (data.status || '').toLowerCase();
      const isSuccess = status === 'success';
      const isPartial = status === 'partial_success';

      if (isSuccess || isPartial) {
        const s3Deleted = Array.isArray(data.s3FilesDeleted) ? data.s3FilesDeleted.length : (report.s3FilesDeleted || 0);
        const s3Failed = Array.isArray(data.s3FilesFailed) ? data.s3FilesFailed.length : (report.s3FilesFailed || 0);

        console.log('User deletion completed:', {
          user: selectedUser,
          reason: deleteReason,
          timestamp: new Date().toISOString(),
          status: data.status,
          totalNodesDeleted: report.totalNodesDeleted || data.deletedCount || 0,
          s3FilesDeleted: s3Deleted,
          gdprCompliant: report.gdprCompliant,
          complianceReport: report
        });

        // Capture the real results snapshot for the Deletion Complete step.
        setDeleteResult({
          status: data.status,
          userEmail: report.userEmail || selectedUser?.email,
          userName: selectedUser?.name,
          totalNodesDeleted: report.totalNodesDeleted ?? data.deletedCount ?? 0,
          s3FilesDeleted: s3Deleted,
          s3FilesFailed: s3Failed,
          completionReason: report.completionReason || '',
          gdprCompliant: report.gdprCompliant,
          auditRecordId: report.auditRecordId || null,
          auditPersisted: report.auditPersisted === true,
          stepResults: report.stepResults || data.cascadeResults || {},
        });

        // Remove user from local state and advance to the success step.
        setUsers(prev => prev.filter(u => u.userId !== selectedUser.userId));
        setDeleteProgress(100);
        setDeleteStep(5);

        // Surface partial-success detail so it is never silently swallowed.
        if (isPartial || s3Failed > 0 || (data.errors && data.errors.length)) {
          const warn = [
            ...(data.errors || []),
            s3Failed > 0 ? `${s3Failed} S3 file(s) could not be deleted` : null
          ].filter(Boolean).join('; ');
          console.warn('User deletion completed with warnings:', warn);
          setError(warn ? `Completed with warnings: ${warn}` : '');
        }
      } else {
        throw new Error(data.errors?.join(', ') || report.completionReason || 'User deletion failed');
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
      // Prefer the server's own error text (e.g. protection guard rejections)
      // over axios's generic "status code 500" message.
      const serverMsg = error.response?.data?.errors?.join(', ');
      setError(`Failed to delete user: ${serverMsg || error.message}`);
      setIsDeleting(false);
      setDeleteProgress(0);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedUser(null);
    setDeleteStep(1);
    setDeleteResult(null);
    setIsDeleting(false);
    setDeleteProgress(0);
  };

  // Use getRoleLabel from hook instead of formatRole
  const formatRole = (role) => getRoleLabel(role) || 'Unknown';

  // Get role filter options from the hook
  const roleFilterOptions = getRoleFilterOptions(true);

  // Validation filter options
  const validationFilterOptions = [
    { key: 'all', value: 'all', text: 'All Users' },
    { key: 'complete', value: 'complete', text: 'Complete Profiles' },
    { key: 'incomplete', value: 'incomplete', text: 'Incomplete Profiles' },
    { key: 'issues', value: 'issues', text: 'Data Issues' }
  ];

  const deleteReasonOptions = [
    { key: 'inactive', value: 'Account inactive for extended period', text: 'Account inactive for extended period' },
    { key: 'violation', value: 'Terms of service violation', text: 'Terms of service violation' },
    { key: 'duplicate', value: 'Duplicate account', text: 'Duplicate account' },
    { key: 'request', value: 'User deletion request', text: 'User deletion request' },
    { key: 'spam', value: 'Spam or fraudulent account', text: 'Spam or fraudulent account' },
    { key: 'other', value: 'Other (specify in notes)', text: 'Other (specify in notes)' }
  ];

  const renderDeleteStep = () => {
    switch (deleteStep) {
      case 1:
        return (
          <div>
            <Header as="h3" color="red">
              <Icon name="warning sign" />
              Step 1: Deletion Reason
            </Header>
            <Message warning>
              <Message.Header>Important</Message.Header>
              <p>User deletion is permanent and cannot be undone. Please provide a detailed reason for this action.</p>
            </Message>
            <div >
              <Form style={{ width: "100%" }}>
                <Form.Group style={{ width: "100%" }}>
                  <div style={{ width: "300px", marginRight: "20px" }}>
                    <label>Select Reason Category</label>
                    <Dropdown
                      placeholder="Select a reason category"
                      fluid
                      selection
                      options={deleteReasonOptions}
                      onChange={(e, { value }) => setDeleteReason(value)}
                    />
                  </div>
                  {/* <Form.Field style={{ width: "300px", backgroundColor: "#ffa600ff" }} > */}
                  <TextArea
                    style={{ width: "400px" }}
                    cols={40}
                    label="Additional Details"
                    placeholder="Provide additional context for this deletion..."
                    value={deleteReason}
                    onChange={(e, { value }) => setDeleteReason(value)}
                    rows={4}
                  />
                  {/* </Form.Field> */}

                </Form.Group>
              </Form>
            </div>
            <Message info style={{ width: '100%' }}>
              <p>Minimum 10 characters required. This information will be logged for audit purposes.</p>
            </Message>
          </div>
        );

      case 2:
        return (
          <div>
            <Header as="h3" color="orange">
              <Icon name="clipboard check" />
              Step 2: Pre-Deletion Checklist
            </Header>
            <Message warning>
              <Message.Header>Required Actions</Message.Header>
              <p>Complete all required actions before proceeding with deletion.</p>
            </Message>
            <div className="vertical-form-container">
              <Form className="vertical-form">
                <Form.Field className="checkbox-field noMarginBottom">
                  <Checkbox
                    label="I understand that this action will permanently delete all user data, profiles, messages, and relationships"
                    checked={confirmationChecks.understandConsequences}
                    onChange={(e, { checked }) => handleConfirmationChange('understandConsequences', checked)}
                  />
                </Form.Field>
                <Form.Field className="checkbox-field noMarginBottom">
                  <Checkbox
                    label="I have created a backup of critical user data if needed for legal or business purposes"
                    checked={confirmationChecks.backupCreated}
                    onChange={(e, { checked }) => handleConfirmationChange('backupCreated', checked)}
                  />
                </Form.Field>
                <Form.Field className="checkbox-field ">
                  <Checkbox
                    label="I have notified relevant stakeholders about this user deletion"
                    checked={confirmationChecks.notificationsSent}
                    onChange={(e, { checked }) => handleConfirmationChange('notificationsSent', checked)}
                  />
                </Form.Field>
              </Form>
            </div>


          </div>
        );

      case 3:
        return (
          <div>
            <Header as="h3" color="red">
              <Icon name="exclamation triangle" />
              Step 3: Final Confirmation
            </Header>
            <Message negative>
              <Message.Header>FINAL WARNING</Message.Header>
              <p>You are about to permanently delete user <strong>{selectedUser?.name}</strong>. This action cannot be undone.</p>
            </Message>
            <Segment>
              <Header as="h4">User Details to be Deleted:</Header>
              <List>
                <List.Item><strong>Name:</strong> {selectedUser?.name}</List.Item>
                <List.Item><strong>Email:</strong> {selectedUser?.email}</List.Item>
                <List.Item><strong>Role:</strong> {formatRole(selectedUser?.role || selectedUser?.primaryRole)}</List.Item>
                <List.Item><strong>User ID:</strong> {selectedUser?.userId}</List.Item>
                <List.Item><strong>Deletion Reason:</strong> {deleteReason}</List.Item>
              </List>
            </Segment>
            <Form>
              <Form.Field>
                <Checkbox
                  label={`I confirm that I want to permanently delete user ${selectedUser?.name} and all associated data`}
                  checked={confirmationChecks.finalConfirmation}
                  onChange={(e, { checked }) => handleConfirmationChange('finalConfirmation', checked)}
                />
              </Form.Field>
            </Form>
          </div>
        );

      case 4:
        return (
          <div>
            <Header as="h3" color="blue">
              <Icon name="spinner" loading />
              Step 4: Deletion in Progress
            </Header>
            <Message info>
              <Message.Header>Processing Deletion</Message.Header>
              <p>Please wait while the user deletion is processed. Do not close this window.</p>
            </Message>
            <Progress percent={deleteProgress} indicating>
              Deleting user data...
            </Progress>
          </div>
        );

      case 5: {
        const r = deleteResult || {};
        const steps = r.stepResults || {};
        const partial = (r.status || '').toLowerCase() === 'partial_success' || (r.s3FilesFailed || 0) > 0;
        return (
          <div>
            <Header as="h3" color={partial ? 'orange' : 'green'}>
              <Icon name={partial ? 'exclamation triangle' : 'check circle'} />
              Deletion {partial ? 'Completed (with warnings)' : 'Complete'}
            </Header>
            <Message positive={!partial} warning={partial}>
              <Message.Header>
                {(r.userEmail || selectedUser?.name || 'User')} {partial ? 'partially removed' : 'permanently deleted'}
              </Message.Header>
              <p>{r.completionReason || 'The account and its data have been removed from the system.'}</p>
            </Message>

            <Segment>
              <Header as="h4">Results snapshot</Header>
              <List horizontal relaxed size="large">
                <List.Item>
                  <Icon name="database" color="blue" />
                  <List.Content><List.Header>{r.totalNodesDeleted ?? 0}</List.Header>DB nodes deleted</List.Content>
                </List.Item>
                <List.Item>
                  <Icon name="cloud" color="blue" />
                  <List.Content><List.Header>{r.s3FilesDeleted ?? 0}</List.Header>S3 files deleted</List.Content>
                </List.Item>
                <List.Item>
                  <Icon name={r.s3FilesFailed ? 'warning circle' : 'check'} color={r.s3FilesFailed ? 'orange' : 'green'} />
                  <List.Content><List.Header>{r.s3FilesFailed ?? 0}</List.Header>S3 files failed</List.Content>
                </List.Item>
              </List>

              {Object.keys(steps).length > 0 && (
                <Table basic="very" compact size="small" style={{ marginTop: '1em' }}>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Cascade step</Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">Deleted</Table.HeaderCell>
                      <Table.HeaderCell textAlign="center">Status</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {Object.entries(steps).map(([key, v]) => (
                      <Table.Row key={key}>
                        <Table.Cell>{STEP_LABELS[key] || key}</Table.Cell>
                        <Table.Cell textAlign="right">{key === 's3Cleanup' ? (v.filesDeleted ?? 0) : (v.deleted ?? 0)}</Table.Cell>
                        <Table.Cell textAlign="center">
                          <Icon name={v.success ? 'check' : 'times'} color={v.success ? 'green' : 'red'} />
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}

              <p style={{ color: '#888', fontSize: '0.85em', marginTop: '0.75em' }}>
                <Icon name="file alternate outline" /> GDPR compliance report generated.
                {r.auditPersisted
                  ? <> A durable deletion-audit record was stored{r.auditRecordId ? <> (id <code>{r.auditRecordId}</code>)</> : null}.</>
                  : <> (Audit record could not be persisted — see server log.)</>}
              </p>
            </Segment>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Container fluid className="landing-page admin-landing">
      <Container style={{ marginTop: "30px" }}>
        <Grid className="articleListWidth">
          <Grid.Row>
            <Grid.Column width={3}>
              <div className="img-with-gradient">
                <img src={warningIcon} alt="" style={{ width: '120px', height: '120px' }} />
              </div>
            </Grid.Column>
            <Grid.Column width={13} textAlign="center">
              <span className="blgListItemTitle">Delete User</span>
              <span className="blgListSubTitle">Permanent User Removal</span>
              <p className="landing-subheading-text">
                Safely and permanently remove user accounts from the system. This process includes
                comprehensive data cleanup and audit logging for compliance.
              </p>
              <div style={{ textAlign: 'left', marginTop: '1em' }}>
                <Button
                  onClick={() => navigate('/admin/user-management')}
                  size="large"
                  className="landing-goto-button"
                  icon="arrow left"
                  content="Back to User Management"
                />
                <Button
                  onClick={loadUsers}
                  size="large"
                  className="landing-goto-button"
                  loading={loading}
                  icon="refresh"
                  content="Refresh User Data"
                  style={{ marginLeft: '1em' }}
                />
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Grid className="articleListWidth" style={{ marginTop: "20px", borderTop: "solid 1px #AAAAAA" }}></Grid>
      </Container>

      <Container style={{ marginTop: '2em', marginBottom: '2em' }}>
        {/* Error Message */}
        {error && (
          <Message negative>
            <Message.Header>Error Loading Users</Message.Header>
            <p>{error}</p>
            <Button onClick={loadUsers} primary>Retry</Button>
          </Message>
        )}

        {/* Warning Message */}
        <Message warning icon>
          <Icon name="warning sign" />
          <Message.Content>
            <Message.Header>Caution: Permanent Action</Message.Header>
            User deletion is irreversible. Ensure you have proper authorization and have followed all required procedures before proceeding.
          </Message.Content>
        </Message>

        {/* User Search and Filter */}
        <Header as="h2" dividing>
          <Icon name="search" />
          <Header.Content>
            Select User for Deletion
            <Header.Subheader>Search and select the user account to delete</Header.Subheader>
          </Header.Content>
        </Header>

        <Grid columns={3} stackable>
          <Grid.Column>
            <Input
              icon="search"
              placeholder="Search users by name, email, or ID..."
              value={searchTerm}
              onChange={(e, { value }) => setSearchTerm(value)}
              fluid
            />
          </Grid.Column>
          <Grid.Column>
            <Dropdown
              placeholder="Filter by role"
              fluid
              selection
              options={roleFilterOptions}
              value={roleFilter}
              onChange={(e, { value }) => setRoleFilter(value)}
            />
          </Grid.Column>
          <Grid.Column>
            <Dropdown
              placeholder="Filter by validation status"
              fluid
              selection
              options={validationFilterOptions}
              value={validationFilter}
              onChange={(e, { value }) => setValidationFilter(value)}
            />
          </Grid.Column>
        </Grid>

        {/* User Table */}
        <Segment loading={loading} style={{ marginTop: '1em' }}>
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>User</Table.HeaderCell>
                <Table.HeaderCell>Role</Table.HeaderCell>
                <Table.HeaderCell>Profile Status</Table.HeaderCell>
                <Table.HeaderCell>Last Active</Table.HeaderCell>
                <Table.HeaderCell>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {filteredUsers.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan="5" textAlign="center">
                    {loading ? 'Loading users...' : 'No users found matching your criteria.'}
                  </Table.Cell>
                </Table.Row>
              ) : (
                filteredUsers.map((user, index) => {
                  const isCurrentAdmin = isCurrentUser(user);
                  return (
                    <Table.Row
                      key={user.userId || index}
                      warning={!user.isProfileComplete}
                      disabled={isCurrentAdmin}
                      style={isCurrentAdmin ? { opacity: 0.6, backgroundColor: '#f8f8f8' } : {}}
                    >
                      <Table.Cell>
                        <Header as="h4" image>
                          <Icon name={getRoleIcon(user.role || user.primaryRole)} />
                          <Header.Content>
                            {user.name || 'Unknown Name'}
                            {isCurrentAdmin && (
                              <Label size="mini" color="blue" style={{ marginLeft: '0.5em' }}>
                                <Icon name="user" />
                                Current User
                              </Label>
                            )}
                            {!user.isProfileComplete && (
                              <Icon
                                name="warning sign"
                                color="orange"
                                title="Profile validation issues"
                                style={{ marginLeft: '0.5em' }}
                              />
                            )}
                            <Header.Subheader>
                              {user.email || 'No email'}
                              <br />
                              <small>ID: {user.userId || 'No ID'}</small>
                            </Header.Subheader>
                          </Header.Content>
                        </Header>
                      </Table.Cell>
                      <Table.Cell>
                        <Label color={getRoleColor(user.role || user.primaryRole)}>
                          <Icon name={getRoleIcon(user.role || user.primaryRole)} />
                          {formatRole(user.role || user.primaryRole)}
                        </Label>
                      </Table.Cell>
                      <Table.Cell>
                        {user.isProfileComplete ? (
                          <Label color="green">
                            <Icon name="check circle" />
                            Complete
                          </Label>
                        ) : (
                          <div>
                            <Label color="orange">
                              <Icon name="warning sign" />
                              Incomplete
                            </Label>
                            {user.validationIssues && user.validationIssues.length > 0 && (
                              <div style={{ marginTop: '0.5em' }}>
                                {user.validationIssues.slice(0, 2).map((issue, idx) => (
                                  <Label key={idx} size="mini" color="red" style={{ marginBottom: '0.2em', display: 'block' }}>
                                    {issue}
                                  </Label>
                                ))}
                                {user.validationIssues.length > 2 && (
                                  <Label size="mini" color="grey">
                                    +{user.validationIssues.length - 2} more issues
                                  </Label>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {user.lastLoginDate ? new Date(user.lastLoginDate).toLocaleDateString() : 'Never'}
                      </Table.Cell>
                      <Table.Cell>
                        {isCurrentAdmin ? (
                          <Button
                            icon="shield"
                            disabled
                            content="Protected"
                            size="small"
                            title="Cannot delete your own administrator account"
                          />
                        ) : (
                          <Button
                            icon="trash"
                            color="red"
                            onClick={() => handleDeleteUser(user)}
                            content="Delete User"
                            size="small"
                          />
                        )}
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              )}
            </Table.Body>
          </Table>
        </Segment>

        {/* User count */}
        {filteredUsers.length > 0 && (
          <div style={{ textAlign: 'center', marginTop: '1em' }}>
            <Label>
              Showing {filteredUsers.length} of {users.length} users
            </Label>
          </div>
        )}
      </Container>

      {/* Multi-Step Delete Confirmation Modal */}
      <Modal
        className='modalWidth'
        open={showDeleteModal}
        onClose={!isDeleting ? closeDeleteModal : undefined}
        size="large"
        closeOnDimmerClick={!isDeleting}
        closeOnEscape={!isDeleting}
      >
        <Modal.Header>
          <Icon name="warning sign" color="red" />
          Delete User: {selectedUser?.name || 'Unknown'}
        </Modal.Header>
        <Modal.Content scrolling>
          <Step.Group ordered fluid>
            <Step active={deleteStep === 1} completed={deleteStep > 1}>
              <Step.Content>
                <Step.Title>Reason</Step.Title>
                <Step.Description>Provide deletion reason</Step.Description>
              </Step.Content>
            </Step>
            <Step active={deleteStep === 2} completed={deleteStep > 2}>
              <Step.Content>
                <Step.Title>Checklist</Step.Title>
                <Step.Description>Complete pre-deletion tasks</Step.Description>
              </Step.Content>
            </Step>
            <Step active={deleteStep === 3} completed={deleteStep > 3}>
              <Step.Content>
                <Step.Title>Confirm</Step.Title>
                <Step.Description>Final confirmation</Step.Description>
              </Step.Content>
            </Step>
            <Step active={deleteStep === 4} completed={deleteStep > 4}>
              <Step.Content>
                <Step.Title>Delete</Step.Title>
                <Step.Description>Processing deletion</Step.Description>
              </Step.Content>
            </Step>
            <Step active={deleteStep === 5}>
              <Step.Content>
                <Step.Title>Complete</Step.Title>
                <Step.Description>Deletion finished</Step.Description>
              </Step.Content>
            </Step>
          </Step.Group>

          <Divider />

          {renderDeleteStep()}
        </Modal.Content>
        <Modal.Actions>
          {deleteStep < 4 && (
            <Button onClick={closeDeleteModal} disabled={isDeleting}>
              Cancel
            </Button>
          )}
          {deleteStep > 1 && deleteStep < 4 && (
            <Button onClick={() => setDeleteStep(prev => prev - 1)} disabled={isDeleting}>
              <Icon name="arrow left" />
              Previous
            </Button>
          )}
          {deleteStep < 3 && (
            <Button
              primary
              onClick={proceedToNextStep}
              disabled={!canProceedToNextStep() || isDeleting}
            >
              Next
              <Icon name="arrow right" />
            </Button>
          )}
          {deleteStep === 3 && (
            <Button
              color="red"
              onClick={executeUserDeletion}
              disabled={!canProceedToNextStep() || isDeleting}
            >
              <Icon name="trash" />
              Delete User Permanently
            </Button>
          )}
          {deleteStep === 5 && (
            <Button primary onClick={closeDeleteModal}>
              <Icon name="check" />
              Done
            </Button>
          )}
        </Modal.Actions>
      </Modal>

      {AuthStdFooter()}
    </Container>
  );
};

export default DeleteUser;
